export const CAMPAIGN = {
  id: 'connect-2026-11',
  endsAt: '2026-12-03T06:00:00Z',
  url: 'https://www.utilityprofit.com/connect',
  supportedPms: ['AppFolio', 'Buildium', 'Rent Manager'],
};

export function getActor(user) {
  if (!user || !user.id) return null;
  return {
    id: user.partnerViewAs?.actorUserId || user.impersonatingActorUserId || user.id,
    isUpAdmin: Boolean(user.partnerViewAs || user.impersonatingActorUserId) || (user.roles || []).includes('admin'),
  };
}

const normalizePms = (name) => (name || '').toLowerCase().replace(/[^a-z]/g, '');

export function getAnnouncementContent(name) {
  const pms = CAMPAIGN.supportedPms.find((supported) => normalizePms(supported) === normalizePms(name));
  const destination = pms || 'your property management software';
  const examplePms = pms || 'AppFolio';
  const images = { AppFolio: 'appfolio', Buildium: 'buildium', 'Rent Manager': 'rent-manager' };
  return {
    pms,
    examplePms,
    image: `/images/up-connect/${images[examplePms]}.png`,
    headline: `Your tenants’ utility setup in ${destination}.`,
    topBar: `See your tenants’ utility status, providers, and account numbers in ${destination}.`,
    modalBar: `See this tenant’s utility setup in ${destination}.`,
  };
}

export function isAnnouncementPreview({ isUpAdmin, appUrl, search }) {
  if (!isUpAdmin) return false;
  try {
    return /^sunroom-[a-z0-9-]+\.develop\.utilityprofit\.com$/.test(new URL(appUrl).hostname)
      && new URLSearchParams(search).get('announcementPreview') === '1';
  } catch (error) {
    return false;
  }
}

export function getPmsEligibility(name) {
  if (!name || ['', 'unknown', 'other', 'none'].includes(name.toLowerCase().trim())) return 'unknown';
  return CAMPAIGN.supportedPms.some((pms) => normalizePms(pms) === normalizePms(name))
    ? 'supported' : 'unsupported';
}

export function isCampaignActive(disabled, now = Date.now()) {
  return disabled !== 'true' && disabled !== true && now < Date.parse(CAMPAIGN.endsAt);
}

export function storageKey(actorId, placement, field) {
  return [`up-announcement:${CAMPAIGN.id}:${actorId}`, placement, field].filter(Boolean).join(':');
}

const placements = ['top_bar', 'slide_out', 'modal_bar'];
const validCount = (value) => (Number.isSafeInteger(value) && value > 0 ? value : 0);

export function readState(storage, actorId) {
  try {
    let legacy = {};
    try { legacy = JSON.parse(storage.getItem(storageKey(actorId)) || '{}') || {}; } catch (error) { /* A corrupt legacy record must not hide newer placement state. */ }
    return placements.reduce((state, placement) => {
      const saved = legacy[placement];
      const valid = Boolean(saved && typeof saved === 'object' && !Array.isArray(saved));
      const shown = storage.getItem(storageKey(actorId, placement, 'shown')) ?? null;
      const dismissed = storage.getItem(storageKey(actorId, placement, 'dismissed')) ?? null;
      const impressions = storage.getItem(storageKey(actorId, placement, 'impressions')) ?? null;
      if (!valid && shown !== 'true' && dismissed !== 'true' && !validCount(Number(impressions))) return state;
      return {
        ...state,
        [placement]: {
          shown: shown === 'true' || (valid && saved.shown === true),
          dismissed: dismissed === 'true' || (valid && saved.dismissed === true),
          impressions: Math.max(valid ? validCount(saved.impressions) : 0, validCount(Number(impressions))),
        },
      };
    }, {});
  } catch (error) {
    return {};
  }
}

export function writePlacement(storage, actorId, placement, patch) {
  // Separate monotonic flags prevent another tab's exposure from undoing a dismissal.
  if (patch.shown) storage.setItem(storageKey(actorId, placement, 'shown'), 'true');
  if (patch.dismissed) storage.setItem(storageKey(actorId, placement, 'dismissed'), 'true');
  if (validCount(patch.impressions)) {
    const current = readState(storage, actorId)[placement]?.impressions || 0;
    storage.setItem(storageKey(actorId, placement, 'impressions'), String(Math.max(current, patch.impressions)));
  }
}

export function isActorStorageKey(key, actorId) {
  const prefix = storageKey(actorId);
  return key === null || key === prefix || key.startsWith(`${prefix}:`);
}

export function canShowPlacement(state, placement, isUpAdmin, seenThisVisit = false) {
  return state[placement]?.dismissed !== true && !(isUpAdmin && state[placement]?.shown && !seenThisVisit);
}

export function shouldObserveObstructions({ enabled, state, isUpAdmin, seenThisVisit, slideOpen }) {
  if (!enabled) return false;
  const available = (placement) => canShowPlacement(state, placement, isUpAdmin, seenThisVisit.has(placement));
  return ['top_bar', 'modal_bar'].some((placement) => available(placement) && !seenThisVisit.has(placement))
    || (available('slide_out') && (slideOpen || !state.slide_out?.shown));
}

export function updatePlacement(state, placement, patch) {
  return { ...state, [placement]: { ...state[placement], ...patch } };
}

export function shouldDeferSlideOut({ hasModal, viewportWidth, supportPanelHeight }) {
  return hasModal || (viewportWidth <= 600 && supportPanelHeight > 120);
}
